<template>
  <div>
    <div v-if="!folderButtonClicked" class="centerbox">
      <b-row class="header-style">
        <b-col class="text-center font-weight-bold">
          {{ numberOfSelectedThreads }}{{ $getText('MESSAGE_CHOSEN_HEADER') }}
        </b-col>
      </b-row>
      <div class="center-buttons">
        <div class="thread-actions">
          <template v-if="!hasDraftThreadsSelected">
            <b-button v-if="!showMarkButton" variant="link" class="big-button" @click="markSelectedMessages()">
              <i class="icon-Aula_star" />
              <div class="button-text text-center">
                {{ $getText('MESSAGE_MARK') }}
              </div>
            </b-button>
            <b-button v-else variant="link" class="big-button" @click="markSelectedMessages(false)">
              <i class="icon-Aula_star_solid colored" />
              <div class="button-text">
                <span class="unmark-text">{{ $getText('MESSAGE_REMOVE_MARK') }}</span>
                <span class="small-unmark-text">{{ $getText('MESSAGE_REMOVE') }}</span>
              </div>
            </b-button>
            <b-button variant="link" class="big-button" @click="onChangeSubscriptionStatus">
              <i :class="isAllUnread ? 'icon-Aula_envelope_open' : 'icon-Aula_envelope_closed'" />
              <div class="button-text text-center">
                {{ isAllUnread ? $getText('MESSAGE_MARK_AS_READ_SHORT') : $getText('MESSAGE_MARK_AS_UNREAD_SHORT') }}
              </div>
            </b-button>
            <b-button v-if="canMoveToFolder" variant="link" class="big-button" @click="folderButtonClicked = true">
              <i class="icon-Aula_folder" />
              <div class="button-text text-center">
                {{ $getText('MESSAGE_ARCHIVATE') }}
              </div>
            </b-button>
          </template>
          <b-button variant="link" class="big-button" @click="showDeletionWarning">
            <i class="icon-Aula_bin" />
            <div class="button-text text-center">
              {{ $getText('MESSAGE_BUTTON_DELETE') }}
            </div>
          </b-button>
        </div>
        <div class="thread-deselection">
          <b-button variant="link" class="clear-button" @click="unselectMessages">
            <Icon :name="iconClassEnum.CLOSE" />
            <span class="button-text">{{ $t('button.clear_selected') }}</span>
          </b-button>
        </div>
      </div>
    </div>
    <move-to-folder-modal
      v-if="folderButtonClicked"
      :thread-ids="selectedThreadIds"
      :thread-folder-ids="selectedThreadFolderIds"
      :subscription-ids="selectedBundleIds"
      :chosen-folder-and-mail-owner="chosenFolderAndMailOwner"
      :folders="!!chosenFolderAndMailOwner.mailOwnerId ? commonInboxFolders : folders"
      @hide="changeMoveToFolderVisibility"
    />
    <aula-modal
      ref="deleteModal"
      header-text="MESSAGE_DELETE_MORE_THREADS_HEADER"
      ok-text="MESSAGE_DELETE_OK"
      data-size="small"
      @cancelClicked="$refs.deleteModal.hide()"
      @okClicked="deleteSelectedMessages()"
    >
      {{ $getText('MESSAGE_DELETE_MORE_THREADS_WARNING_1') }}<br />
      {{ deletedThreadWarningMessage }} <br />
      {{ $getText('MESSAGE_DELETE_MORE_THREADS_WARNING_2') }}
    </aula-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { types } from '../../store/types/types';
import moment from 'moment-timezone';
import MessageMoveToFolderModal from './MessageMoveToFolderModal.vue';
import { permissionEnum } from '../../../shared/enums/permissionEnum.ts';
import { subscriptionStatus } from '../../../shared/enums/subscriptionStatus';
import { subscriptionTypes } from '../../../shared/enums/subscriptionTypes';
import { messageProviderKeyEnum } from '../../../shared/enums/messageProviderKeyEnum';
import { portalRoles } from '../../../shared/enums/portalRoles';
import Icon from '../../../shared/components/Icon.vue';
import { iconClassEnum } from '../../../shared/enums/iconClassEnum';
import { deselectAllManagers } from '../../../shared/composables/sortableRegistry';
import { useUserProfileStore } from '../../../shared/stores';

export default {
  inject: {
    getDeletedFolderId: messageProviderKeyEnum.GET_DELETED_FOLDER_ID,
    getCanMoveToDeletedMessageFolder: messageProviderKeyEnum.CAN_MOVE_TO_DELETED_MESSAGE_FOLDER,
    deleteOtpSubscriptions: messageProviderKeyEnum.DELETE_OTP_SUBSCRIPTIONS,
    handleSubscriptionDeleted: messageProviderKeyEnum.HANDLE_SUBSCRIPTION_DELETED,
  },
  data: function () {
    return {
      moment: moment,
      folderButtonClicked: false,
      selectedFolder: null,
      isAllUnread: false,
    };
  },
  computed: {
    iconClassEnum() {
      return iconClassEnum;
    },
    ...mapGetters({
      chosenFolderAndMailOwner: types.MESSAGES_GET_CHOSEN_FOLDER_AND_MAIL_OWNER,
      selectedThreadIds: types.MESSAGES_GET_CHOSEN_THREAD_IDS,
      selectedThreadFolderIds: types.MESSAGES_GET_CHOSEN_THREAD_FOLDER_IDS,
      selectedSubscriptionIds: types.MESSAGES_GET_CHOSEN_SUBSCRIPTION_IDS,
      selectedBundleIds: types.MESSAGES_GET_CHOSEN_BUNDLE_IDS,
      commonInboxFolders: types.MESSAGES_GET_FOLDERS_FOR_CURRENT_COMMON_INBOX,
      folders: types.MESSAGES_GET_FOLDERS,
      threadBundles: types.MESSAGES_GET_THREAD_BUNDLES,
      hasPermission: types.HAS_PERMISSION,
      activeChildren: types.GET_ACTIVE_CHILDREN,
      activeInstitutions: types.GET_ACTIVE_INSTITUTIONS,
      subscriptionsAndBundleThreads: types.MESSAGES_GET_SUBSCRIPTIONS_AND_BUNDLE_THREADS,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    canMoveToDeletedMessageFolder() {
      return this.getCanMoveToDeletedMessageFolder();
    },
    deletedFolderId() {
      return this.getDeletedFolderId();
    },
    deletedThreadWarningMessage() {
      let message = this.$getText('MESSAGE_DELETE_MORE_THREADS_INFO_2');

      if (this.chosenFolderAndMailOwner?.mailOwnerId) {
        message = this.$getText('MESSAGE_DELETE_MORE_THREADS_INFO_2_COMMON');
      }
      if (this.canMoveToDeletedMessageFolder) {
        message += ` ${this.$getText('MESSAGE_DELETE_MORE_THREADS_INFO_FROM_NORMAL_FOLDER')}`;
      }
      return message;
    },
    numberOfSelectedThreads() {
      // AULA-37947: count the number of draft messages also
      let count = this.selectedThreadIds.length;
      this.selectedBundleIds.forEach(bundleId => {
        const bundleExists = this.threadBundles.some(bundle => bundle.bundleId === bundleId);
        if (bundleExists === false) {
          const bundle = this.subscriptionsAndBundleThreads.find(
            item => item.subscriptionId === bundleId && item.subscriptionType === subscriptionTypes.BUNDLE
          );
          if (bundle != null) {
            count = count + bundle.numberOfBundleItems;
          }
        }
      });
      return count;
    },
    showMarkButton() {
      let containsUnmarkedMessages = false;
      for (const subscription of this.selectedThreadIds) {
        const subscriptionChosen = this.subscriptionsAndBundleThreads.filter(
          item => item.id === subscription && item.marked
        );
        if (subscriptionChosen.length > 0) {
          containsUnmarkedMessages = true;
          return containsUnmarkedMessages;
        }
      }
      return containsUnmarkedMessages;
    },
    canMoveToFolder() {
      return this.hasPermission(permissionEnum.INBOX_FOLDERS);
    },
    hasDraftThreadsSelected() {
      const draftThreads = this.selectedThreadIds.filter(subscription => subscription.toString().match(/^DRAFT/));
      return draftThreads.length > 0;
    },
  },
  methods: {
    ...mapActions({
      updateSelectedThreadIds: types.ACTION_UPDATE_SELECTED_THREAD_IDS,
      updateSelectedThreadFolderIds: types.ACTION_UPDATE_SELECTED_THREAD_FOLDER_IDS,
      updateSelectedSubscriptionIds: types.ACTION_UPDATE_SELECTED_SUBSCRIPTION_IDS,
      updateSelectedBundleIds: types.ACTION_UPDATE_SELECTED_BUNDLE_IDS,
      updateThreadViewAction: types.UPDATE_THREADS_VIEW,
      switchMarkMessages: types.SWITCH_MARK_SUBSCRIPTIONS,
      deleteSubscriptions: types.DELETE_SUBSCRIPTIONS,
      updateSubscriptionStatus: types.MESSAGES_UPDATE_SUBSCRIPTION_STATUS,
      loadNotifications: types.LOAD_NOTIFICATIONS,
      leaveThreads: types.MESSAGES_LEAVE_THREADS,
      moveThreadsToFolder: types.MOVE_THREADS_TO_FOLDER,
    }),
    ...mapMutations({
      uncheckAllSubscriptions: types.MUTATE_UNCHECK_ALL_SUBSCRIPTIONS,
      mutateDeleteDraftThread: types.MUTATE_DELETE_DRAFT_THREAD,
      deleteSubscriptionById: types.MUTATE_DELETE_SUBSCRIPTION_BY_ID,
    }),
    showDeletionWarning() {
      this.$refs.deleteModal.show();
    },
    changeMoveToFolderVisibility(value) {
      this.folderButtonClicked = value;
    },
    folderSelected(folderId) {
      this.selectedFolder = folderId;
    },
    async deleteSelectedMessages() {
      this.$refs.deleteModal.hide();
      const draftThreadIds = this.selectedThreadIds.filter(item => item.match && item.match(/^DRAFT/));
      const allSelectedSubscriptionIds = [...this.selectedBundleIds, ...this.selectedSubscriptionIds];
      if (this.canMoveToDeletedMessageFolder) {
        if (allSelectedSubscriptionIds.length > 0) {
          allSelectedSubscriptionIds.forEach(subscriptionId => this.deleteSubscriptionById({ subscriptionId }));
          await this.leaveThreads({
            subscriptionIds: allSelectedSubscriptionIds,
          });
          await this.moveThreadsToFolder({
            threadIds: this.selectedThreadIds,
            subscriptionIds: allSelectedSubscriptionIds,
            folderId: this.deletedFolderId,
          });
          this.$showToast(this.$tc('messages.message_deleted', allSelectedSubscriptionIds.length));
          this.handleSubscriptionDeleted(allSelectedSubscriptionIds);
        }
        for (const threadId of draftThreadIds) {
          this.mutateDeleteDraftThread({
            id: this.profile.id,
            key: threadId,
          });
        }
      } else if (this.profile.role == portalRoles.OTP) {
        this.deleteOtpSubscriptions(this.subscriptionsAndBundleThreads, this.deleteSubscriptions);
      } else {
        this.deleteSubscriptions({
          threadIds: this.selectedThreadIds,
          subscriptionIds: this.selectedBundleIds,
        });
      }
      this.loadNotifications({
        activeChildrenIds: this.activeChildren,
        activeInstitutionCodes: this.activeInstitutions,
      });
      this.unselectMessages();
    },
    markSelectedMessages(isMarked = true) {
      this.switchMarkMessages({
        threadIds: this.selectedThreadIds,
        subscriptionIds: this.selectedBundleIds,
        isMarked: isMarked,
      }).then(() => {
        this.unselectMessages();
      });
    },
    async onChangeSubscriptionStatus() {
      const selectedSubscriptions = this.subscriptionsAndBundleThreads.filter(
        subscription =>
          this.selectedThreadIds.includes(subscription.id) && subscription.subscriptionType !== subscriptionTypes.BUNDLE
      );
      const selectedSubscriptionIds = selectedSubscriptions.map(subscription => subscription.subscriptionId);
      const subscriptionIdsToUpdate = selectedSubscriptionIds.concat(this.selectedBundleIds);
      const statusToUpdate = this.isAllUnread ? subscriptionStatus.READ : subscriptionStatus.UNREAD;

      await this.updateSubscriptionStatus({
        subscriptionIds: subscriptionIdsToUpdate,
        status: statusToUpdate,
      });

      this.isAllUnread = !this.isAllUnread;
      this.unselectMessages();

      this.loadNotifications({
        activeChildrenIds: this.activeChildren,
        activeInstitutionCodes: this.activeInstitutions,
      });
    },
    unselectMessages() {
      this.uncheckAllSubscriptions();
      this.updateSelectedThreadIds([]);
      this.updateSelectedThreadFolderIds([]);
      this.updateSelectedSubscriptionIds([]);
      this.updateSelectedBundleIds([]);
      deselectAllManagers();
    },
    setIsAllUnreadStatus() {
      const selectedSubscriptions = this.subscriptionsAndBundleThreads.filter(subscription => {
        if (subscription.subscriptionType === subscriptionTypes.BUNDLE) {
          return this.selectedBundleIds.includes(subscription.subscriptionId);
        }
        return this.selectedThreadIds.includes(subscription.id);
      });
      this.isAllUnread = selectedSubscriptions.every(subscription => subscription.read === false);
    },
  },
  watch: {
    selectedThreadIds() {
      this.setIsAllUnreadStatus();
    },
    selectedBundleIds() {
      this.setIsAllUnreadStatus();
    },
  },
  mounted() {
    this.setIsAllUnreadStatus();
  },
  components: {
    Icon,
    'move-to-folder-modal': MessageMoveToFolderModal,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/elements/modal' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/core/animations' as *;

.big-button {
  width: 72px;
  border: none;
  background: none;
  font-size: 32px;
  padding: 8px 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;
  transition-property: transform, box-shadow;
  transition-timing-function: var(--ease-in-out);
  transition-duration: 300ms;

  &:hover {
    background-color: var(--color-white);
    box-shadow: var(--box-shadow-base);
    transform: translateY(-2px);
  }

  .theme-employee & {
    color: $color-primary-darker-employee;
  }

  .theme-guardian & {
    color: $color-primary-darker-guardian;
  }

  .theme-child & {
    color: $color-primary-darker-child;
  }

  .theme-admin & {
    color: $color-primary-darker-admin;
  }
}

.button-text {
  font-size: 1rem;
  width: 100%;

  .theme-employee & {
    color: $color-primary-darker-employee;
  }

  .theme-guardian & {
    color: $color-primary-darker-guardian;
  }

  .theme-child & {
    color: $color-primary-darker-child;
  }

  .theme-admin & {
    color: $color-primary-darker-admin;
  }
}

.center-buttons {
  margin-top: 3%;
}

.centerbox {
  margin-top: 140px;
}

.header-style {
  margin-top: 10%;
}

.container-scroll {
  overflow: auto;
}

.colored {
  color: $color_alert;
}

.link-style {
  color: $color-primary-dark;
  font-weight: bold;
}

.unmark-text {
  display: none;
  @include breakpoint-xs-up() {
    display: initial;
  }
}

.small-unmark-text {
  display: initial;
  @include breakpoint-sm-up() {
    display: none;
  }
}

.sletTextWidth {
  width: 50px !important;
}
.thread-actions {
  display: flex;
  justify-content: center;
  border-bottom: 1px solid var(--color-grey-dunkel);
  width: fit-content;
  margin: auto;
  padding-bottom: 32px;
  gap: 4px;
}
.thread-deselection {
  display: flex;
  justify-content: center;
  padding-top: 28px;
  .clear-button {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1rem;
    padding: 4px 8px;
    border-radius: 8px;
    &:hover {
      background-color: var(--color-white);
    }
  }
}
</style>
